export interface ModuleColorConfig {
  accent: string;
  bg: string;
  label: string;
}

/*
 * Color de identidad por módulo (rediseño 2, 2026-09-25).
 *
 * Es la FUENTE ÚNICA del par accent+bg de cada sección: lo consumen la cinta
 * de cabecera de cada pantalla y la pastilla del icono en el raíl. Hasta el
 * 2026-09-25 el raíl tenía su propia copia en tokens `--tile-*` de
 * `admin-tokens.css`, y las dos ya habían divergido (Categorías era ámbar
 * aquí y verde allí). Se borraron esos tokens: `App.tsx` lee de aquí y pasa
 * los valores como variables inline. Misma regla que las etiquetas de rol —
 * no volver a duplicarlo.
 *
 * Los valores salen del mapa `MOD` del documento de diseño, que asigna un
 * tono de identidad por sección y no repite ninguno. Las secciones que el
 * documento dibuja y Kivai no tiene (Colas, Personas, Buscar, Conocimiento)
 * no están aquí: ver K29. Su «Métricas» es, desde el paso 88, `informes`.
 *
 * Regla: el color de acción (--color-primary) no identifica secciones. Si un
 * elemento es tinta es porque se puede pulsar, no porque pertenezca a una
 * sección. La excepción es Tickets, que en el documento ES la tinta — y se
 * distingue porque nunca aparece como fondo dominante.
 *
 * El accent va en las LETRAS del título de la cinta clara y en el icono de la
 * pastilla — nunca como fondo. Todos pasan AA sobre su propio `bg`.
 */
export const MODULE_COLORS: Record<string, ModuleColorConfig> = {
  home:        { accent: '#26333F', bg: '#ECEEF0', label: 'Inicio' },
  incidencias: { accent: '#001D33', bg: '#E6EAEE', label: 'Tickets' },
  asistente:   { accent: '#9D2F76', bg: '#FCE7F2', label: 'Asistente' },
  areas:       { accent: '#0E7285', bg: '#E3F4F7', label: 'Equipos' },
  categorias:  { accent: '#2F6B41', bg: '#E6F1E9', label: 'Categorías' },
  /* Paso 85a. Los dos pares son de `TILE_COLORS` (`rowVisuals.ts`), ya
     calibrados a AA, y ninguno lo usaba otra sección. Brown y sky, y no
     purple: purple quedaría pegado a Usuarios (indigo) en el raíl y se
     leerían como el mismo tono. */
  clientes:    { accent: '#7A4A22', bg: '#F5EAE0', label: 'Clientes' },
  usuarios:    { accent: '#4B4AC2', bg: '#ECECFB', label: 'Usuarios' },
  tecnicos:    { accent: '#1A6A9E', bg: '#E2EFF7', label: 'Técnicos' },
  roles:       { accent: '#8A5A16', bg: '#FAF0DC', label: 'Roles' },
  /* Paso 88. El par del diseño v7 (`kivai-tickets-v7-informes.html`, MOD
     informes): 7.0:1 sobre su fondo. No es el verde de «Métricas» del v6,
     que ya es Categorías (regla de UNICIDAD del CLAUDE.md). */
  informes:    { accent: '#5B3FA8', bg: '#EFEAFB', label: 'Informes' },
  /* Paso 89. Ningún par libre de `TILE_COLORS` servía: purple queda pegado a
     Informes y slate es el de reserva. Oliva, 78° de tono: el hueco más
     grande de la rueda entre Roles (35°) y Categorías (138°), y lejos del
     rojo de «vencido» y del ámbar de «queda poco», que son los colores del
     SLA en la bandeja. 5.41:1 sobre su fondo, 6.10:1 sobre blanco. */
  sla:         { accent: '#4F6B0F', bg: '#EEF4DD', label: 'SLA' },
  /* Paso 92a. NO es el color del v6 (#5B3FA8, que desde el 88 es Informes)
     ni el de la IA de staff (`asistente`): es OTRO asistente, el del portal
     del cliente, y no deben leerse como el mismo. Verde mar, 165°: el hueco
     entre Categorías (138°) y Equipos (190°). 5.96:1 sobre su fondo, 6.93:1
     sobre blanco. */
  asistentePortal: { accent: '#0B6650', bg: '#E0F2EB', label: 'Asistente del portal' },
  /* Paso 101. El gris pizarra, el de «configuración» y de reserva: Parámetros
     ES la configuración, y es la única entrada del raíl que lo usa. */
  parametros:  { accent: '#40494F', bg: '#F1F2F4', label: 'Parámetros' },

  /* Fallback para ids desconocidos (p. ej. `origin` de una sugerencia del
     agente que no mapea a un módulo). */
  configuracion: { accent: '#40494F', bg: '#F1F2F4', label: 'Configuración' },
};

export function getModuleColor(moduleId: string): ModuleColorConfig {
  return MODULE_COLORS[moduleId] ?? MODULE_COLORS.configuracion!;
}
